// ============================================================
// MainLayout — Sidebar + Header con navegación completa
// ============================================================
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Layout, Menu, Avatar, Dropdown, Badge, Typography,
  Space, Tag, Tooltip,
} from "antd";
import {
  DashboardOutlined, FileTextOutlined, ThunderboltOutlined,
  ProjectOutlined, TrophyOutlined, CheckSquareOutlined,
  BarChartOutlined, UploadOutlined, SettingOutlined,
  UserOutlined, LogoutOutlined, BellOutlined,
  RocketOutlined, MenuFoldOutlined, MenuUnfoldOutlined,
} from "@ant-design/icons";
// import { useKeycloak } from "@react-keycloak/web";
import { useKeycloak } from "../../App";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const NAV_ITEMS = [
  {
    key: "/",
    icon: <DashboardOutlined />,
    label: "Dashboard",
  },
  {
    key: "iabp",
    icon: <ThunderboltOutlined />,
    label: "IABP",
    children: [
      { key: "/solicitudes", label: "Solicitudes de Áreas" },
      { key: "/quick-wins", label: "Quick Wins" },
    ],
  },
  {
    key: "/proyectos",
    icon: <ProjectOutlined />,
    label: "Proyectos Activos",
  },
  {
    key: "/oportunidades",
    icon: <TrophyOutlined />,
    label: "Oportunidades (D1)",
  },
  {
    key: "/tareas",
    icon: <CheckSquareOutlined />,
    label: "Mis Tareas",
  },
  {
    key: "/reporting",
    icon: <BarChartOutlined />,
    label: "Reporting Ejecutivo",
  },
  {
    key: "/importacion",
    icon: <UploadOutlined />,
    label: "Importar Planner",
  },
  {
    key: "/admin",
    icon: <SettingOutlined />,
    label: "Administración",
  },
];

interface Props {
  children: React.ReactNode;
}

export default function MainLayout({ children }: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { keycloak } = useKeycloak();

  const userName = keycloak?.tokenParsed?.name ||
                 keycloak?.tokenParsed?.preferred_username || "Usuario";

  const userMenu = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "Mi perfil",
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Cerrar sesión",
      danger: true,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === "logout") {
      keycloak?.logout();
    }
  };

  // Determinar keys activos y abiertos
  const selectedKey = location.pathname;
  const openKeys = NAV_ITEMS
    .filter((item) => item.children?.some((c) => c.key === selectedKey))
    .map((item) => item.key);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={240}
        style={{
          background: "#1a1f5e",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
          overflowY: "auto",
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: collapsed ? "16px 8px" : "16px 20px",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
            cursor: "pointer",
          }}
          onClick={() => navigate("/")}
        >
          <Space>
            <RocketOutlined style={{ color: "#fff", fontSize: 20 }} />
            {!collapsed && (
              <div>
                <Text style={{ color: "#fff", fontWeight: 700, display: "block", lineHeight: 1.2, fontSize: 13 }}>
                  ATLAS
                </Text>
                <Text style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>
                  Mapping your path to success
                </Text>
              </div>
            )}
          </Space>
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          defaultOpenKeys={openKeys}
          items={NAV_ITEMS}
          onClick={({ key }) => { if (!key.includes("iabp")) navigate(key); }}
          style={{ background: "transparent", borderRight: 0, marginTop: 8 }}
        />
      </Sider>

      {/* ── Main ────────────────────────────────────────────────────── */}
      <Layout style={{ marginLeft: collapsed ? 80 : 240, transition: "margin 0.2s" }}>
        {/* Header */}
        <Header
          style={{
            background: "#fff",
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #f0f0f0",
            position: "sticky",
            top: 0,
            zIndex: 99,
          }}
        >
          <Space>
            {collapsed
              ? <MenuUnfoldOutlined onClick={() => setCollapsed(false)} style={{ fontSize: 18, cursor: "pointer" }} />
              : <MenuFoldOutlined onClick={() => setCollapsed(true)} style={{ fontSize: 18, cursor: "pointer" }} />
            }
            <Tag color="#1a1f5e" style={{ margin: 0 }}>
              IT & Transformación Digital Spain
            </Tag>
          </Space>

          <Space size={16}>
            <Tooltip title="Notificaciones">
              <Badge count={3} size="small">
                <BellOutlined style={{ fontSize: 18, cursor: "pointer" }} />
              </Badge>
            </Tooltip>

            <Dropdown
              menu={{ items: userMenu, onClick: handleMenuClick }}
              placement="bottomRight"
            >
              <Space style={{ cursor: "pointer" }}>
                <Avatar
                  size="small"
                  style={{ background: "#1a1f5e" }}
                  icon={<UserOutlined />}
                />
                <Text style={{ fontSize: 13 }}>{userName}</Text>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        {/* Content */}
        <Content
          style={{
            padding: "24px",
            background: "#f5f5f5",
            minHeight: "calc(100vh - 64px)",
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
}
